import React from 'react';
import { PiPlus, PiTrash } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  ActionInputType,
  getSchemaEnumValues,
  getSchemaProps,
  getSchemaRequired,
  isPlainObject,
  normalizeSchemaType,
} from './workflow-schema-utils';

type SchemaFieldDef = {
  key: string;
  required: boolean;
  type: ActionInputType;
  enumValues?: Array<string | number>;
  schema?: Record<string, unknown>;
  description?: string;
};

const buildSchemaFields = (
  schema?: Record<string, unknown>
): SchemaFieldDef[] => {
  if (!schema) return [];
  const props = getSchemaProps(schema);
  const required = getSchemaRequired(schema);

  return Object.entries(props)
    .map(([key, value]) => {
      const schemaObj = isPlainObject(value)
        ? (value as Record<string, unknown>)
        : undefined;
      return {
        key,
        required: required.has(key),
        type: normalizeSchemaType(schemaObj),
        enumValues: schemaObj ? getSchemaEnumValues(schemaObj) : undefined,
        schema: schemaObj,
        description:
          schemaObj && typeof schemaObj.description === 'string'
            ? schemaObj.description
            : undefined,
      } satisfies SchemaFieldDef;
    })
    .sort((a, b) => a.key.localeCompare(b.key));
};

const getAdditionalPropsConfig = (
  schema?: Record<string, unknown>
): { allowed: boolean; schema?: Record<string, unknown> } => {
  if (!schema) return { allowed: true, schema: undefined };
  const additional = (schema as { additionalProperties?: unknown })
    .additionalProperties;
  if (additional === false || additional === null) {
    return { allowed: false, schema: undefined };
  }
  if (additional === true || additional === undefined) {
    return { allowed: true, schema: undefined };
  }
  if (isPlainObject(additional)) {
    return { allowed: true, schema: additional as Record<string, unknown> };
  }
  return { allowed: true, schema: undefined };
};

const coerceValueWithSchema = (
  raw: string,
  schemaHint?: Record<string, unknown>
): unknown => {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const typeHint = normalizeSchemaType(schemaHint);
  if (typeHint === 'number' || typeHint === 'integer') {
    const parsed =
      typeHint === 'integer'
        ? Number.parseInt(trimmed, 10)
        : Number.parseFloat(trimmed);
    return Number.isNaN(parsed) ? raw : parsed;
  }
  if (typeHint === 'boolean') {
    if (trimmed.toLowerCase() === 'true') return true;
    if (trimmed.toLowerCase() === 'false') return false;
  }
  if (typeHint === 'object' || typeHint === 'array') {
    try {
      return JSON.parse(trimmed);
    } catch {
      return typeHint === 'array' ? [] : {};
    }
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
};

const defaultValueForSchema = (
  schemaHint?: Record<string, unknown>
): unknown => {
  const typeHint = normalizeSchemaType(schemaHint);
  if (typeHint === 'number' || typeHint === 'integer') return 0;
  if (typeHint === 'boolean') return false;
  if (typeHint === 'array') return [];
  if (typeHint === 'object') return {};
  return '';
};

type LiteralJsonEditorProps = {
  fieldKey: string;
  value: unknown;
  onChange: (next: unknown) => void;
  schema?: Record<string, unknown>;
  formOnly?: boolean;
  forceValueType?: 'object' | 'array';
};

export const LiteralJsonEditor: React.FC<LiteralJsonEditorProps> = ({
  fieldKey,
  value,
  onChange,
  schema,
  forceValueType,
}) => {
  const schemaFields = React.useMemo(() => buildSchemaFields(schema), [schema]);
  const hasSchemaFields = schemaFields.length > 0;
  const additionalConfig = React.useMemo(
    () => getAdditionalPropsConfig(schema),
    [schema]
  );
  const allowAdditional = additionalConfig.allowed !== false;
  const additionalSchema = additionalConfig.schema;
  const isFreeObject = !hasSchemaFields && allowAdditional;
  const forbidObjectProps =
    schema !== undefined && !allowAdditional && !hasSchemaFields;
  const arrayItemSchema =
    schema && isPlainObject((schema as { items?: unknown }).items)
      ? ((schema as { items?: unknown }).items as Record<string, unknown>)
      : undefined;

  const renderSchemaFieldInput = (
    field: SchemaFieldDef,
    current: unknown,
    update: (val: unknown) => void
  ) => {
    if (field.enumValues && field.enumValues.length > 0) {
      const numericEnum = field.enumValues.every((v) => typeof v === 'number');
      return (
        <select
          className="border rounded px-2 py-1.5 text-sm w-full"
          value={
            typeof current === 'string' || typeof current === 'number'
              ? String(current)
              : ''
          }
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              update(undefined);
              return;
            }
            update(numericEnum ? Number(raw) : raw);
          }}
        >
          <option value="">Selecciona…</option>
          {field.enumValues.map((opt) => (
            <option key={`${field.key}-${String(opt)}`} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'boolean') {
      return (
        <select
          className="border rounded px-2 py-1.5 text-sm w-full"
          value={typeof current === 'boolean' ? String(current) : ''}
          onChange={(e) => {
            if (e.target.value === '') {
              update(undefined);
              return;
            }
            update(e.target.value === 'true');
          }}
        >
          <option value="">Sin valor</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    if (field.type === 'number' || field.type === 'integer') {
      return (
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          value={typeof current === 'number' ? String(current) : ''}
          onChange={(e) => {
            if (e.target.value === '') {
              update(undefined);
              return;
            }
            const parsed =
              field.type === 'integer'
                ? Number.parseInt(e.target.value, 10)
                : Number(e.target.value);
            update(Number.isNaN(parsed) ? undefined : parsed);
          }}
          placeholder="valor"
        />
      );
    }

    if (field.type === 'string') {
      return (
        <input
          className="border rounded px-2 py-1.5 text-sm w-full"
          value={typeof current === 'string' ? current : ''}
          onChange={(e) => update(e.target.value || undefined)}
          placeholder="texto"
        />
      );
    }

    if (field.type === 'object') {
      return (
        <LiteralJsonEditor
          fieldKey={`${fieldKey}.${field.key}`}
          value={current}
          schema={field.schema}
          formOnly
          forceValueType="object"
          onChange={update}
        />
      );
    }

    if (field.type === 'array') {
      return (
        <LiteralJsonEditor
          fieldKey={`${fieldKey}.${field.key}`}
          value={current}
          schema={field.schema}
          formOnly
          forceValueType="array"
          onChange={update}
        />
      );
    }

    return (
      <input
        className="border rounded px-2 py-1.5 text-sm w-full"
        value={
          typeof current === 'string' ||
          typeof current === 'number' ||
          typeof current === 'boolean'
            ? String(current)
            : ''
        }
        onChange={(e) =>
          update(coerceValueWithSchema(e.target.value, field.schema))
        }
        placeholder="valor"
      />
    );
  };

  const renderSchemaObjectForm = () => {
    const objValue = isPlainObject(value)
      ? (value as Record<string, unknown>)
      : {};

    return (
      <div className="space-y-3">
        {schemaFields.map((field) => {
          const current = objValue[field.key];
          const updateField = (nextVal: unknown) => {
            const next: Record<string, unknown> = { ...objValue };
            if (nextVal === undefined || nextVal === '') {
              delete next[field.key];
            } else {
              next[field.key] = nextVal;
            }
            onChange(next);
          };

          return (
            <div
              key={`${fieldKey}-${field.key}`}
              className="space-y-1 border border-slate-200 rounded p-3 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-800 font-medium">
                    {field.key}
                  </span>
                  {field.required && (
                    <span className="text-[11px] text-rose-600">
                      (requerido)
                    </span>
                  )}
                  <span className="text-[11px] text-slate-500">
                    · {field.type}
                  </span>
                </div>
                {!field.required && (
                  <Button
                    type="button"
                    variant="neutral"
                    appearance="ghost"
                    size="sm"
                    onClick={() => updateField(undefined)}
                  >
                    <div className="flex items-center gap-1">
                      <PiTrash className="w-4 h-4" />
                      <span>Limpiar</span>
                    </div>
                  </Button>
                )}
              </div>
              {field.description && (
                <Text size="tiny" className="text-slate-500">
                  {field.description}
                </Text>
              )}
              {renderSchemaFieldInput(field, current, updateField)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFreeObjectForm = () => {
    const baseValue =
      isPlainObject(value) && !forceValueType
        ? (value as Record<string, unknown>)
        : {};
    const objValue =
      forceValueType === 'object' && !isPlainObject(value) ? {} : baseValue;
    const entries = Object.entries(objValue);

    return (
      <div className="space-y-2">
        {entries.length === 0 && (
          <Text size="tiny" className="text-slate-500">
            Sin claves. Agrega un campo para empezar.
          </Text>
        )}
        {entries.map(([k, v], idx) => (
          <div
            key={`${fieldKey}-pair-${k}-${idx}`}
            className="border border-slate-200 rounded-lg bg-white p-3 space-y-2 shadow-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <input
                className="border rounded px-2 py-1.5 text-sm w-full"
                value={k}
                onChange={(e) => {
                  const nextKey = e.target.value;
                  if (!nextKey) return;
                  const next: Record<string, unknown> = { ...objValue };
                  const oldVal = next[k];
                  delete next[k];
                  next[nextKey] = oldVal;
                  onChange(next);
                }}
                placeholder="clave"
              />
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="sm"
                isIconOnly
                onClick={() => {
                  const next: Record<string, unknown> = { ...objValue };
                  delete next[k];
                  onChange(next);
                }}
              >
                <PiTrash className="w-4 h-4" />
              </Button>
            </div>
            <input
              className="border rounded px-2 py-1.5 text-sm w-full"
              value={
                typeof v === 'string' ||
                typeof v === 'number' ||
                typeof v === 'boolean'
                  ? String(v)
                  : JSON.stringify(v)
              }
              onChange={(e) => {
                const next: Record<string, unknown> = { ...objValue };
                next[k] = coerceValueWithSchema(
                  e.target.value,
                  additionalSchema
                );
                onChange(next);
              }}
              placeholder="valor"
            />
          </div>
        ))}
        <Button
          type="button"
          variant="primary"
          appearance="outline"
          size="sm"
          onClick={() => {
            const next: Record<string, unknown> = { ...objValue };
            let idx = entries.length;
            while (next[`campo_${idx}`] !== undefined) idx += 1;
            next[`campo_${idx}`] = defaultValueForSchema(additionalSchema);
            onChange(next);
          }}
        >
          <div className="flex items-center gap-2">
            <PiPlus className="w-4 h-4" />
            <span>Agregar campo</span>
          </div>
        </Button>
      </div>
    );
  };

  const renderObjectForm = () => {
    if (forbidObjectProps) {
      return (
        <Text size="tiny" className="text-slate-500">
          Este esquema no permite propiedades adicionales.
        </Text>
      );
    }
    if (hasSchemaFields) return renderSchemaObjectForm();
    return renderFreeObjectForm();
  };

  const renderArrayForm = () => {
    const arr =
      forceValueType === 'array' && !Array.isArray(value)
        ? ([] as unknown[])
        : Array.isArray(value)
          ? (value as unknown[])
          : [];
    return (
      <div className="space-y-2">
        {arr.length === 0 && (
          <Text size="tiny" className="text-slate-500">
            Sin ítems. Agrega un elemento para empezar.
          </Text>
        )}
        {arr.map((item, idx) => (
          <div
            key={`${fieldKey}-item-${idx}`}
            className="border border-slate-200 rounded-lg bg-white p-3 space-y-2 shadow-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <Text size="tiny" className="text-slate-600">
                Ítem {idx + 1}
              </Text>
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="sm"
                isIconOnly
                onClick={() => {
                  const next = [...arr];
                  next.splice(idx, 1);
                  onChange(next);
                }}
              >
                <PiTrash className="w-4 h-4" />
              </Button>
            </div>
            <input
              className="border rounded px-2 py-1.5 text-sm w-full"
              value={
                typeof item === 'string' ||
                typeof item === 'number' ||
                typeof item === 'boolean'
                  ? String(item)
                  : JSON.stringify(item)
              }
              onChange={(e) => {
                const next = [...arr];
                next[idx] = coerceValueWithSchema(
                  e.target.value,
                  arrayItemSchema
                );
                onChange(next);
              }}
              placeholder="valor"
            />
          </div>
        ))}
        <Button
          type="button"
          variant="primary"
          appearance="outline"
          size="sm"
          onClick={() =>
            onChange([...arr, defaultValueForSchema(arrayItemSchema)])
          }
        >
          <div className="flex items-center gap-2">
            <PiPlus className="w-4 h-4" />
            <span>Agregar ítem</span>
          </div>
        </Button>
      </div>
    );
  };

  const renderFormMode = () => {
    if (forceValueType === 'object') return renderObjectForm();
    if (forceValueType === 'array') return renderArrayForm();
    if (isPlainObject(value)) return renderObjectForm();
    if (Array.isArray(value)) return renderArrayForm();
    if (isFreeObject) return renderObjectForm();
    return null;
  };

  return (
    <div className="border border-slate-200 rounded bg-slate-50 p-3 space-y-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <Text size="small" weight="medium" className="text-slate-800">
          JSON / objeto
        </Text>
        <Text size="tiny" className="text-slate-500">
          Se guarda tal cual lo escribas
        </Text>
      </div>
      {renderFormMode()}
    </div>
  );
};

export default LiteralJsonEditor;
